import { useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { Link } from "react-router-dom";

import { supabase } from "../../../lib/supabase";

interface Mentor {
  uuid: string;
  name: string;
  email: string;
  specialization: string;
  meetings_held: number;
  date_joined: string;
}

export function MentorList() {
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadMentors() {
      try {
        const { data, error } = await supabase.from("mentor").select(`
            user_uuid,
            specialization,
            meetings_held,
            vincer_base_user!inner (
              uuid,
              name,
              email,
              date_joined
            )
          `);

        if (error) throw error;

        // Transform the nested data structure into flat Mentor objects
        const formattedMentors: Mentor[] = data.map((mentor) => ({
          uuid: mentor.user_uuid,
          name: mentor.vincer_base_user.name,
          email: mentor.vincer_base_user.email,
          specialization: mentor.specialization,
          meetings_held: mentor.meetings_held,
          date_joined: new Date(
            mentor.vincer_base_user.date_joined
          ).toLocaleDateString(),
        }));

        setMentors(formattedMentors);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "An error occurred while loading mentors"
        );
      } finally {
        setLoading(false);
      }
    }

    loadMentors();
  }, []);

  if (loading) {
    return <div className="py-8 text-center">Loading mentors...</div>;
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Mentors</h1>
        <Link
          to="/coordinator/mentors/add"
          className="inline-flex items-center rounded-md border border-transparent bg-[#4CAF50] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#45a049] focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:ring-offset-2"
        >
          <UserPlus className="mr-2 size-5" />
          Add Mentor
        </Link>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Specialization
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Meetings Held
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Joined
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {mentors.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  No mentors found. Add your first mentor to get started!
                </td>
              </tr>
            ) : (
              mentors.map((mentor) => (
                <tr key={mentor.uuid} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                    {mentor.name}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {mentor.email}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {mentor.specialization}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {mentor.meetings_held}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {mentor.date_joined}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
